import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { FiShield, FiUsers, FiKey, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import DashboardCardGrid from '../components/DashboardCardGrid';
import { HOME_CARD_DATA } from './CardGrid';
import { EMPLOYEE_CARD_DATA } from '../Employeedata/Employeecard';
import { GLOBAL_SETTINGS_CARD_DATA } from '../GlobalSettings/CardsGrid';
import './Dashboard.css';

const ALL_HUB_CARDS = [
  ...HOME_CARD_DATA,
  ...EMPLOYEE_CARD_DATA,
  ...GLOBAL_SETTINGS_CARD_DATA,
];

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const cardHoverSx = {
  cursor: 'pointer',
  position: 'relative',
  height: '100%',
  '&:before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(to right, #0048e6, #9C15F7, rgba(255, 75, 248, 0.3))',
    opacity: 0,
    zIndex: 0,
    transition: 'opacity 0.25s ease',
  },
  '&:hover': {
    '&:before': {
      opacity: 0.28,
    },
    '& .card-arrow': {
      color: '#4f46e5',
    },
  },
};

const SECTION_CONFIG = {
  'admin-control': {
    title: 'Admin Control',
    subtitle: 'Admins, tickets, SOP post, disinfection time, and robot images',
    paths: [
      '/create-admin',
      '/admin-user',
      '/RobotMsgList',
      '/TicketForm',
      '/SOP-POST',
      '/disinfectionTime',
      '/Viewimage',
    ],
  },
  'user-control': {
    title: 'User Control',
    subtitle: 'Users, robots, metadata, and cache settings',
    paths: [
      '/create-user',
      '/robot-form',
      '/users-list',
      '/user-robot',
      '/update-apppermession',
      '/CacheSettings',
      '/metadata-Change',
    ],
  },
  'spotless-access': {
    title: 'Spotless Access',
    subtitle: 'Bookings, tickets, SOP list, and employee data',
    paths: [
      '/Robot-Booking',
      '/Booking-details',
      '/EmployeeList',
      '/SOP',
      '/employee-data/all',
      '/EmployeeDateRange',
    ],
  },
};

const CONTROL_CARDS = [
  {
    id: 'admin-control',
    title: 'Admin Control',
    description:
      'Manage administrators, tickets, disinfection time, and robot images.',
    meta: ['Update Ticket', 'Disinfection Time', 'Robot images'],
    icon: <FiShield size={28} />,
    accent: '#0ea5e9',
  },
  {
    id: 'user-control',
    title: 'User Control',
    description:
      'Manage end users, assignments, metadata, and operational access.',
    meta: ['Create User', 'Update Metadata', 'Cache settings'],
    icon: <FiUsers size={28} />,
    accent: '#8b5cf6',
  },
  {
    id: 'spotless-access',
    title: 'Spotless Access',
    description:
      'Bookings, ticket views, SOP list, and employee data workflows.',
    meta: ['View Ticket', 'SOP List', 'Employee data'],
    icon: <FiKey size={28} />,
    accent: '#f59e0b',
  },
];

function BackToHubButton({ onClick }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<FiArrowLeft size={16} />}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        color: '#475569',
        mb: 0.5,
        px: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: 'transparent',
          color: '#4f46e5',
        },
      }}
    >
      Back to Control Hub
    </Button>
  );
}

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  const sectionCards = useMemo(() => {
    if (!activeSection) return [];
    const config = SECTION_CONFIG[activeSection];
    if (!config) return [];
    const pathSet = new Set(config.paths);
    return ALL_HUB_CARDS.filter((card) => pathSet.has(card.path));
  }, [activeSection]);

  if (activeSection && SECTION_CONFIG[activeSection]) {
    const config = SECTION_CONFIG[activeSection];
    return (
      <DashboardCardGrid
        pageTitle={config.title}
        pageSubtitle={config.subtitle}
        cardData={sectionCards}
        headerTrailing={<BackToHubButton onClick={() => setActiveSection(null)} />}
      />
    );
  }

  return (
    <Box className="control-hub">
      <Box className="control-hub__header">
        <Box className="control-hub__header-inner">
          <Typography component="h1" className="control-hub__title">
            Control Hub
          </Typography>
          <Typography className="control-hub__subtitle">
            Choose Admin Control, User Control, or Spotless Access
          </Typography>
        </Box>
      </Box>

      <Box className="control-hub__content">
        <Grid
          container
          spacing={3}
          sx={{
            animation: `${fadeInUp} 0.5s ease-out forwards`,
          }}
        >
          {CONTROL_CARDS.map((card, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={card.id}
              sx={{
                display: 'flex',
                animation: `${fadeInUp} 0.4s ease-out ${index * 0.05}s forwards`,
                opacity: 0,
              }}
            >
              <Card
                className="control-hub__card"
                onClick={() => setActiveSection(card.id)}
                sx={cardHoverSx}
                elevation={0}
              >
                <CardContent className="control-hub__card-body">
                  <Box
                    className="control-hub__icon"
                    style={{
                      color: card.accent,
                      backgroundColor: `${card.accent}14`,
                    }}
                  >
                    {card.icon}
                  </Box>

                  <Typography component="h2" className="control-hub__card-title">
                    {card.title}
                  </Typography>
                  <Typography className="control-hub__card-desc">
                    {card.description}
                  </Typography>

                  <ul className="control-hub__meta">
                    {card.meta.map((item) => (
                      <li key={item}>
                        <span
                          className="control-hub__meta-dot"
                          style={{ backgroundColor: card.accent }}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <Box className="card-arrow control-hub__arrow">
                    <span>Open</span>
                    <FiArrowRight size={16} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
